import type Viewer from "~/js/asset-viewport/viewer";
import Editor from "~/js/editor";
import type { SocketOut } from "~/js/graph/node-socket-connection";
import * as HoverFeatureMenuMethods from "./methods";
import { closeHoverFeatureMenu, openHoverFeatureMenuFromViewer } from "./methods";
import type { HoverFeatureMenuContext } from "./model";
import { ViewerType } from "./model";

// Function to initialize hover feature menu for any viewer
export const initHoverFeatureMenuForViewer = (
	viewer: Viewer,
	viewerType: ViewerType,
	socketsOutput: SocketOut,
	socketName: string
) => {
	const domElement = viewer.domElement();
	if (!domElement) return;

	const openMenu = async () => {
		const rect = domElement.getBoundingClientRect();
		const zoom = Editor.context.graph.navigation.getZoomScale();
		const asset = await viewer.getAsset();
		const assetName = asset?.name;
		const context: HoverFeatureMenuContext = {
			viewerType,
			nodeUuid: socketsOutput.node.uuid,
			socketName,
			size: {
				width: rect.width / zoom,
				height: rect.height / zoom
			},
			asset: () => viewer.getAsset(),
			assetName,
			socketsOutput,
			viewer,
			onClose: () => {},
			container: viewer.hoverFeatureMount(),
			node: socketsOutput.node
		};
		openHoverFeatureMenuFromViewer(context);
	};

	const handleMouseEnter = () => {
		openMenu();
	};

	const handleMouseLeave = () => {
		HoverFeatureMenuMethods.closeHoverFeatureMenuFromViewer();
	};

	// Add event listeners
	domElement.addEventListener("mouseenter", handleMouseEnter);
	domElement.addEventListener("mouseleave", handleMouseLeave);

	// Cleanup function
	return () => {
		domElement.removeEventListener("mouseenter", handleMouseEnter);
		domElement.removeEventListener("mouseleave", handleMouseLeave);
		closeHoverFeatureMenu();
	};
};

// Specific integration functions for each viewer type
export const initImageViewerHoverMenu = (viewer: any, socketsOutput: any, socketName: string) => {
	return initHoverFeatureMenuForViewer(viewer, ViewerType.Image, socketsOutput, socketName);
};

export const initSvgViewerHoverMenu = (viewer: any, socketsOutput: any, socketName: string) => {
	return initHoverFeatureMenuForViewer(viewer, ViewerType.Svg, socketsOutput, socketName);
};

export const initTextViewerHoverMenu = (viewer: any, socketsOutput: any, socketName: string) => {
	return initHoverFeatureMenuForViewer(viewer, ViewerType.Text, socketsOutput, socketName);
};

export const initAudioViewerHoverMenu = (viewer: any, socketsOutput: any, socketName: string) => {
	return initHoverFeatureMenuForViewer(viewer, ViewerType.Audio, socketsOutput, socketName);
};

export const initVideoViewerHoverMenu = (viewer: any, socketsOutput: any, socketName: string) => {
	return initHoverFeatureMenuForViewer(viewer, ViewerType.Video, socketsOutput, socketName);
};

export const initObject3DViewerHoverMenu = (
	viewer: any,
	socketsOutput: any,
	socketName: string
) => {
	return initHoverFeatureMenuForViewer(viewer, ViewerType.Object3D, socketsOutput, socketName);
};
